/* eslint-disable react/prop-types */
import * as React from 'react'
import * as styles from './bread.crumbs.styl'
import cx from 'classnames'
import { NavLink } from 'react-router-dom'
import BreadcrumbsMaterialUI from '@mui/material/Breadcrumbs'
import Chip from '@mui/material/Chip'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { CSSSelector } from '@src/utils/css.selector.helper'

interface TypographyProps {
  label: any
}
export interface LinkProps {
  to: any
  title: React.ReactNode
  onClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
}
export interface ItemProps {
  title?: string
  type?: ItemType
  isCollapsed?: boolean
  onClick?: (event: Event) => void
}
const Typography: React.FunctionComponent<TypographyProps> = ({ label }) => (
  <span className={styles.typography}>{label}</span>
)

export enum ItemType {
  text,
  chip
}

const ItemComponent = {
  [ItemType.text]: Typography,
  [ItemType.chip]: Chip
}

export interface Props {
  id?: string
  children?: React.ReactNode
}

const BreadCrumbsCSSSelectors = {
  breadCrumbsSelector: new CSSSelector('breadCrumbs'),
  breadCrumbsItemSelector: new CSSSelector('breadCrumbs-item')
}

const BreadCrumbs: React.FC<React.PropsWithChildren<Props>> = React.memo(
  (props: React.PropsWithChildren<Props>) => {
    const { children, id } = props

    return (
      <div
        id={id}
        role={BreadCrumbsCSSSelectors.breadCrumbsSelector.value}
        className={cx(styles.breadcrumbs)}
      >
        <BreadcrumbsMaterialUI
          separator={<span className={styles.dot} />}
          arial-label="Breadcrumb"
        >
          {children}
        </BreadcrumbsMaterialUI>
      </div>
    )
  }
)

const BreadcrumbsLink: React.FC<React.PropsWithChildren<LinkProps>> =
  React.memo((props: React.PropsWithChildren<LinkProps>) => {
    const { to, title, children, onClick } = props

    function handleClick(
      event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) {
      if (onClick && typeof onClick === 'function') {
        onClick(event)
      }
    }

    return (
      <NavLink
        onClick={handleClick}
        role={BreadCrumbsCSSSelectors.breadCrumbsItemSelector.value}
        to={to}
      >
        <>{title || children}</>
      </NavLink>
    )
  })

const BreadcrumbsItem: React.FC<React.PropsWithChildren<ItemProps>> =
  React.memo((props: React.PropsWithChildren<ItemProps>) => {
    const {
      title,
      type = ItemType.text,
      isCollapsed,
      onClick,
      children
    } = props
    const Component = ItemComponent[type]
    const text = title || children || ''
    const isTextChildren = typeof children === 'string'
    const desc = title || isTextChildren ? (children as string) : ''

    return (
      <div
        title={desc}
        role={BreadCrumbsCSSSelectors.breadCrumbsItemSelector.value}
        className={styles.item}
      >
        <Component
          label={<span className={styles.text}>{text}</span>}
          onDelete={onClick}
          deleteIcon={isCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          className={cx(styles.typography)}
        />
      </div>
    )
  })

export { BreadcrumbsLink, BreadcrumbsItem }

export default BreadCrumbs
